import { Component, ElementRef, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { BaseClass } from 'src/app/shared/models/base-class';
import { LanguageService } from 'src/app/shared/services/common/language.service';
import { mimeTypes } from 'mime-wrapper';
import { FileService } from 'src/app/shared/services/rest/file.service';
import { catchError, debounceTime, filter, last, map, switchMap, tap } from 'rxjs/operators';
import { LayoutService } from 'src/app/shared/services/common/layout.service';
import { SnackBarItem } from '../../../snack-bar/snack-bar-item';
import { marker } from '@biesbjerg/ngx-translate-extract-marker';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { interval, of, ReplaySubject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AiService } from 'src/app/shared/services/rest/ai.service';

@Component({
  selector: 'app-file-text-to-speech',
  templateUrl: './file-text-to-speech.component.html',
  styleUrls: ['./file-text-to-speech.component.scss']
})
export class FileTextToSpeechComponent extends BaseClass implements OnInit, OnDestroy {

  // @ViewChild("text") text: ElementRef<any>;
  public mimeTypes = mimeTypes;
  public lang: any;
  public activeLang: any;
  public timeZone = new Date().getTimezoneOffset()*60;

  public activeIndex = new FormControl(0);

  public models$: ReplaySubject<any> = new ReplaySubject<any>(1);
  public modelControl: FormControl = new FormControl();
  public models;

  public aiModels$: ReplaySubject<any> = new ReplaySubject<any>(1);
  public aiModelControl: FormControl = new FormControl();
  public aiModels;

  public vibes = [
    {
      id: 1,
      name: 'Calm',
      value: `Voice Affect: Calm, composed, and reassuring; project quiet authority and confidence.

Tone: Sincere, empathetic, and gently authoritative—express genuine apology while conveying competence.

Pacing: Steady and moderate; unhurried enough to communicate care, yet efficient enough to demonstrate professionalism.`
    },
    {
      id: 2,
      name: 'Dramatic',
      value: `Voice Affect: Low, hushed, and suspenseful; convey tension and intrigue.

Tone: Deeply serious and mysterious, maintaining an undercurrent of unease throughout.

Pacing: Slow, deliberate, pausing slightly after suspenseful moments to heighten drama.

Emotion: Restrained yet intense—voice should subtly tremble or tighten at key suspenseful points.

Emphasis: Highlight sensory descriptions ("footsteps echoed," "heart hammering," "shadows melting into darkness") to amplify atmosphere.

Pronunciation: Slightly elongated vowels and softened consonants for an eerie, haunting effect.

Pauses: Insert meaningful pauses after phrases like "only shadows melting into darkness," and especially before the final line, to enhance suspense dramatically.`
    },
    {
      id: 3,
      name: 'Serene',
      value: `Voice Affect: Soft, gentle, soothing; embody tranquility.

Tone: Calm, reassuring, peaceful; convey genuine warmth and serenity.

Pacing: Slow, deliberate, and unhurried; pause gently after instructions to allow the listener time to relax and follow along.

Emotion: Deeply soothing and comforting; express genuine kindness and care.

Pronunciation: Smooth, soft articulation, slightly elongating vowels to create a sense of ease.

Pauses: Use thoughtful pauses, especially between breathing instructions and visualization guidance, enhancing relaxation and mindfulness.`
    },
    {
      id: 4,
      name: 'Sports Coach',
      value: `Voice Affect: Energetic and animated; dynamic with variations in pitch and tone.

Tone: Excited and enthusiastic, conveying an upbeat and thrilling atmosphere. 

Pacing: Rapid delivery when describing the game or the key moments (e.g., "an overtime thriller," "pull off an unbelievable win") to convey the intensity and build excitement.

Slightly slower during dramatic pauses to let key points sink in.

Emotion: Intensely focused, and excited. Giving off positive energy.

Personality: Relatable and engaging. 

Pauses: Short, purposeful pauses after key moments in the game.`
    },
    {
      id: 5,
      name: 'Fitnes Instructor',
      value: `Voice: High-energy, upbeat, and encouraging, projecting enthusiasm and motivation.

Punctuation: Short, punchy sentences with strategic pauses to maintain excitement and clarity.

Delivery: Fast-paced and dynamic, with rising intonation to build momentum and keep engagement high.

Phrasing: Action-oriented and direct, using motivational cues to push participants forward.

Tone: Positive, energetic, and empowering, creating an atmosphere of encouragement and achievement.`
    },
    {
      id: 6,
      name: 'Professional',
      value: `Voice: Clear, authoritative, and composed, projecting confidence and professionalism.

Tone: Neutral and informative, maintaining a balance between formality and approachability.

Punctuation: Structured with commas and pauses for clarity, ensuring information is digestible and well-paced.

Delivery: Steady and measured, with slight emphasis on key figures and deadlines to highlight critical points.`
    },
    {
      id: 7,
      name: 'Cowboy',
      value: `Voice: Warm, relaxed, and friendly, with a steady cowboy drawl that feels approachable.

Punctuation: Light and natural, with gentle pauses that create a conversational rhythm without feeling rushed.

Delivery: Smooth and easygoing, with a laid-back pace that reassures the listener while keeping things clear.

Phrasing: Simple, direct, and folksy, using casual, familiar language to make technical support feel more personable.

Tone: Lighthearted and welcoming, with a calm confidence that puts the caller at ease.`
    },
    {
      id: 8,
      name: 'Auctioner',
      value: `Voice: Staccato, fast-paced, energetic, and rhythmic, with the classic charm of a seasoned auctioneer.

Tone: Exciting, high-energy, and persuasive, creating urgency and anticipation.

Delivery: Rapid-fire yet clear, with dynamic inflections to keep engagement high and momentum strong.

Pronunciation: Crisp and precise, with emphasis on key action words like bid, buy, checkout, and sold to drive urgency.`
    },
    {
      id: 9,
      name: 'Old-Timey',
      value: `Tone: The voice should be refined, formal, and delightfully theatrical, reminiscent of a charming radio announcer from the early 20th century.

Pacing: The speech should flow smoothly at a steady cadence, neither rushed nor sluggish, allowing for clarity and a touch of grandeur.

Pronunciation: Words should be enunciated crisply and elegantly, with an emphasis on vintage expressions and a slight flourish on key phrases.

Emotion: The delivery should feel warm, enthusiastic, and welcoming, as if addressing a distinguished audience with utmost politeness.

Inflection: Gentle rises and falls in pitch should be used to maintain engagement, adding a playful yet dignified flair to each sentence.

Word Choice: The script should incorporate vintage expressions like splendid, marvelous, posthaste, and ta-ta for now, avoiding modern slang.`
    },
    {
      id: 10,
      name: 'Smooth Jazz DJ',
      value: `Voice: The voice should be deep, velvety, and effortlessly cool, like a late-night jazz radio host.

Tone: The tone is smooth, laid-back, and inviting, creating a relaxed and easygoing atmosphere.

Personality: The delivery exudes confidence, charm, and a touch of playful sophistication, as if guiding the listener through a luxurious experience.

Pronunciation: Words should be drawn out slightly with a rhythmic, melodic quality, emphasizing key phrases with a silky flow.

Phrasing: Sentences should be fluid, conversational, and slightly poetic, with pauses that let the listener soak in the cool, jazzy vibe.`
    },

  ]

  public form: FormGroup = this.fb.group({
    text: '',
    voice_id: '',
    model_id: "eleven_flash_v2_5",
    voice_settings: this.fb.group({
      speed: 1,
      stability: 0.5,
      similarity_boost: 0.5,
      style: 0.5,
      use_speaker_boost: true
    })
  })

  public aiForm: FormGroup = this.fb.group({
    provider: 'openai',
    text : '',
    model_id: "",
    voice: "",
    vibe_id: '',
    instructions: "",
    speed: 1
  })

  public aiVoices = [
    { name: 'Alloy', key: 'alloy' },
    { name: 'Ash', key: 'ash' },
    { name: 'Ballad', key: 'ballad' },
    { name: 'Coral', key: 'coral' },
    { name: 'Echo', key: 'echo' },
    { name: 'Fable', key: 'fable' },
    { name: 'Nova', key: 'nova' },
    { name: 'Onyx', key: 'onyx' },
    { name: 'Sage', key: 'sage' },
    { name: 'Shimmer', key: 'shimmer' }
  ];
  

  public loadPreview: boolean = false;
  public isSubmit: boolean = false;
  public result: any;
  public preview: any;
  public voices: any;
  public voicesLegacy: any;
  public voicesShared: any;
  public speaches: any[] = [];
  public host: any = environment.host;
  public ttsPreviewSub: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<FileTextToSpeechComponent>,
    private languageService: LanguageService,
    private aiService: AiService,
    private sanitizer: DomSanitizer,
    private fileService: FileService,
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private dialog: MatDialog,
  ) { super() }

  ngOnInit(): void {
    console.log("FileTextToSpeechComponent", this.data)
    if (this.timeZone > 0) {
      this.timeZone = -this.timeZone
    } else {
      this.timeZone = Math.abs(this.timeZone)
    }
    this.getLangData();
    this.getTextToSpeechModels();
    if (this.data.text) {
      this.form.patchValue({
        text: this.extractTextFromHTML(this.data.text)
      })
      this.aiForm.patchValue({
        text: this.extractTextFromHTML(this.data.text)
      })
    } else {
      if (this.data.file) {
        this.getFile();
      }
    }
    this.getAIModels();
    if (!this.data.notUseLang && this.data.file && this.data.file.parameterValuesToTask) {
      this.lang = this.data.file.parameterValuesToTask.find(k => k.parameter.is_language) && this.data.file.parameterValuesToTask.find(k => k.parameter.is_language).parameterValue ? this.data.file.parameterValuesToTask.find(k => k.parameter.is_language).parameterValue.value : undefined;
    }
    if (this.lang) {
      this.getSharedVoices({company_id: this.data.company.id, language: this.lang}, 'voicesShared')
    } else {
      this.getSharedVoices({company_id: this.data.company.id}, 'voicesShared')
    }

    this.getVoices({show_legacy: true, company_id: this.data.company.id}, 'voicesLegacy')
    this.getVoices({company_id: this.data.company.id}, 'voices')

    this.attachSubscriptions(
      interval(2000).pipe(
        filter(() => !!this.speaches.length),
        switchMap(res => {
          let filterSpeech:any = {
            // company_id: this.data.file ? this.data.file.company_id : this.data.company.id,
            company_id: this.data.company.id,
            id: this.speaches.map(k => k.id).join(',')
          }
          if (this.data.file && this.data.file.company_id != this.data.company.id) {
            filterSpeech.partner_company_id = this.data.file.company_id
          }
          console.log("getTextToSpeech", filterSpeech)
          return this.fileService.getTextToSpeech(filterSpeech).pipe(map(x => x.body))
        }),
        switchMap(results => {
          if (results.filter(k => k.is_done).length) {
            let filterProgress:any = {};

            if (this.data.vidProject) {
              filterProgress.task_id = this.data.vidProject.task_id;
              filterProgress.task_operation_id = this.data.vidProject.task_operation_id;

              if (results.filter(k => k.is_done)[0].save_task_id && results.filter(k => k.is_done)[0].save_task_id != filterProgress.task_id) {
                filterProgress.task_id = results.filter(k => k.is_done)[0].save_task_id
              } 
              if (results.filter(k => k.is_done)[0].save_task_operation_id && results.filter(k => k.is_done)[0].save_task_operation_id != filterProgress.task_operation_id) {
                filterProgress.task_operation_id = results.filter(k => k.is_done)[0].save_task_operation_id
              } 
            } else {
              if (this.data.file && !!this.data.file.task_id) {
                filterProgress.task_id = this.data.file.task_id;
              }
              if (this.data.file && !!this.data.file.task_operation_id) {
                filterProgress.task_operation_id = this.data.file.task_operation_id;
              }
            }



            return this.fileService.getFilesProgressCstExp(this.data.company.id, [...results.filter(k => k.is_done).map(k => k.saved_file_id)], filterProgress,'fileOriginalData,task,taskOperation,createdEmployee,parameterValuesToTask.parameter,parameterValuesToTask.parameterValue').pipe(
              tap(arr => {
                console.log("getTextToSpeech FILES K", arr)
                arr.forEach(element => {
                  results.find(p => p.saved_file_id == element.id).file = element;
                });
                console.log("getTextToSpeech results K", results)
              }),
              map(() => results)
            )
          } else {
            return of(results)
          }
        }),
        tap(resp => {
          // saved_file_id	14973
          console.log("getTextToSpeech sub", resp)
          if (this.speaches[0].is_preview) {
            resp[0].is_preview = true;
          }
          this.speaches = resp;
          if (this.speaches.filter(k => !!k.file).length) {
            if (this.speaches.find(k => !!k.file).is_preview) {
              this.preview = this.speaches.find(k => !!k.file).file;
            } else {
              this.result = this.speaches.find(k => !!k.file).file;
            }
            this.speaches = []
          }
        }),
        switchMap((resp) => {
          if (this.data.file && this.result && !this.result.hasCon) {
            return this.fileService.createFileRelation({
              company_id: this.data.file.company_id,
              file_id: this.data.file.id,
              related_file_id: this.result.id
            }, this.data.company.id).pipe(
              tap(k => {
                this.result.hasCon = true
              }),
              map(() => resp)
            )
          } else {
            return of(resp)
          }
        }),
      ).subscribe(resp => {

      })
    )

    this.attachSubscriptions(
      this.modelControl.valueChanges.pipe(debounceTime(300)).subscribe((resp) => this.onSearchModels(resp))
    )

    this.attachSubscriptions(
      this.form.get('voice_id').valueChanges.subscribe(res => {
        this.form.updateValueAndValidity();
        this.getVoiceSettings(res)
      })
    )

    // this.attachSubscriptions(
    //   this.aiForm.get('voice').valueChanges.subscribe(res => {
    //     if (!!this.aiForm.value.model_id) {
    //       this.start(true, res)
    //     }
    //   })
    // )
    // this.attachSubscriptions(
    //   this.aiForm.get('vibe_id').valueChanges.subscribe(res => {
    //     if (!!this.aiForm.value.model_id) {
    //       this.start(true, null, res)
    //     }
    //   })
    // )

    if (this.data.setting) {
      if (this.data.setting.params.provider && this.data.setting.params.provider == 'openai') {
        this.activeIndex.patchValue(1);
        this.aiForm.patchValue({
            model_id: this.data.setting.params.model_id,
            voice: this.data.setting.params.voice,
            vibe_id: this.data.setting.params.instructions ? this.getVibeByValue(this.data.setting.params.instructions) : '',
            instructions: this.data.setting.params.instructions,
            speed: this.data.setting.params.speed
        })
      } else {
        this.form.patchValue({
          voice_id: this.data.setting.params.voice_id,
          model_id: this.data.setting.params.model_id,
          voice_settings: this.data.setting.params.voice_settings
        })
      }
    }
  }

  getAIModels() {
    this.attachSubscriptions(
      this.aiService.getAIModels({is_text_to_speech: 1}).subscribe(resp => {
        this.aiModels = resp;
        this.aiModels$.next(resp);

        if (this.aiModels.length) {
          this.aiForm.patchValue({
            model_id: this.aiModels[0].id
          })
        }
      })
    )
  }

  onSearchModels(resp) {
    if (!this.models) {
      return;
    }

    if (!resp) {
      this.models$.next(this.models.slice());
      return;
    } else {
      resp = resp.toLowerCase();
    }

    // filter the banks
    this.models$.next(
      this.models.filter(b => b.name.toLowerCase().indexOf(resp) > -1)
    );
  }

  changeTab(e) {
    this.activeIndex.patchValue(e);
    console.log("changeTab e", e);
    console.log("changeTab this.activeIndex.value", this.activeIndex.value);
  }

  getTextToSpeechModels() {
    this.attachSubscriptions(
      this.fileService.getTextToSpeechModels({company_id: this.data.company.id}).pipe(map(x => x.data)).pipe(
        map(res => {
          let x = [];
          res.forEach(el => {
            el.name = el.name.replaceAll("Eleven ", "")
            x.push(el)
          });

          return x;
        })
      ).subscribe(resp => {
        this.models = resp;
        this.models$.next(this.models.slice())
      })
    )
  }

  getModelById(id) {
    if (!this.models || this.models.filter(el => el.model_id == id).length == 0) {
      return false;
    }
    return this.models.find(el => el.model_id == id)
  }

  getVoiceById(id) {
    if (this.voices && this.voices.find(k => k.voice_id == id)) {
      return this.voices.find(k => k.voice_id == id)
    } else if (this.voicesLegacy && this.voicesLegacy.find(k => k.voice_id == id)) {
      return this.voicesLegacy.find(k => k.voice_id == id)
    } else if (this.voicesShared && this.voicesShared.find(k => k.voice_id == id)) {
      return this.voicesShared.find(k => k.voice_id == id)
    } else {
      return null
    }
  }

  resetForm() {
    this.form.patchValue({
      voice_id: '',
      model_id: "eleven_multilingual_v2",
    })

    this.form.get("voice_settings").patchValue({
      speed: 1,
      stability: 0.5,
      similarity_boost: 0.5,
      style: 0.5,
      use_speaker_boost: true
    })
  }

  log() {
    console.log('form', this.form.value);
    console.log('aiForm', this.aiForm.value);
    console.log('result', this.result);
    console.log('speaches', this.speaches);
  }

  getVoiceSettings(v_id) {
    this.attachSubscriptions(
      this.fileService.getVoiceSettings({company_id: this.data.company.id, voice_id: v_id}).pipe(
        map(x => {
          if (x.success) {
            return x.data
          } else {
            return []
          }
        })
      ).subscribe(resp => {
        console.log("getVoiceSettings", resp)
        if (resp) {
          this.form.get("voice_settings").patchValue(resp)
        }
      })
    )
  }

  getVoices(fil, key) {
    this.attachSubscriptions(
      this.fileService.getVoices(fil).pipe(
        map(x => {
          if (x.success) {
            return x.data.voices
          } else {
            return []
          }
        })
      ).subscribe(resp => {
        this[key] = resp
        console.log(key, this[key])
      })
    )
  }

  getSharedVoices(fil, key) {
    this.attachSubscriptions(
      this.fileService.getVoices(fil).pipe(
        map(x => {
          if (x.success) {
            return x.data.voices
          } else {
            return []
          }
        })
      ).subscribe(resp => {
        this[key] = resp
        console.log(key, this[key])
      })
    )
  }

  getFile() {
    this.attachSubscriptions(
      this.fileService.getFilesPreview(this.data.company.id, [this.data.file.id]).pipe(
        map(arr => arr[0])
      ).subscribe(resp => {
        this.data.file = resp
        console.log("RESP", resp)
        console.log("FILE", resp)
        // console.log("text", this.text.nativeElement);
        if (this.data.file.document && this.data.file.document.text) {
          this.form.patchValue({
            text: this.extractTextFromHTML(this.data.file.document.text)
          })
          this.aiForm.patchValue({
            text: this.extractTextFromHTML(this.data.file.document.text)
          })
        }
      })
    )
  }

  extractTextFromHTML(html: string): string {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    return doc.body.textContent || '';
  }

  copySpeech() {
    this.layoutService.showSnackBar({name: 'Speech'}, marker("Copied"), SnackBarItem)
  }

  getVoiceByKey(key):any {
    if (!this.aiVoices || this.aiVoices.filter(el => el.key == key).length == 0) {
      return false;
    }

    return this.aiVoices.find(el => el.key == key)
  }

  getVibeById(id):any {
    if (!this.vibes || this.vibes.filter(el => el.id == id).length == 0) {
      return false;
    }

    return this.vibes.find(el => el.id == id)
  }

  getVibeByValue(value):any {
    if (!this.vibes || this.vibes.filter(el => el.value == value).length == 0) {
      return false;
    }

    return this.vibes.find(el => el.id == value)
  }

  getPreview(event) {
    if (!!this.aiForm.value.model_id) {
      this.preview = undefined;
      this.getTTSPreview()
    }
  }

  getTTSPreview() {
    this.loadPreview = true;
    let startData:any = {
      company_id: this.data.file ? this.data.file.company_id : this.data.company.id,
      params: this.activeIndex.value == 0 ? {...this.form.value}: {...this.aiForm.value},
    }


    if (this.activeIndex.value == 1) {
      // if (!!changeVoice) {
      //   startData.params.voice = changeVoice
      // }
      // if (!!changeVibe) {
      //   startData.params.vibe_id = changeVibe
      // }
      if (!!startData.params.vibe_id) {
        startData.params.instructions = this.getVibeById(startData.params.vibe_id).value
      }

      startData.params.response_format = "mp3";

      console.log('startData', startData);
      console.log('this.aiForm.value', this.aiForm.value);
      startData.params.text = `You have chosen ${this.getVoiceByKey(startData.params.voice).name}'s voice ${startData.params.vibe_id ? this.getVibeById(startData.params.vibe_id).name : 'Default'}'s vibe`
      delete startData.params.vibe_id
    }

    if (this.ttsPreviewSub) {
      this.ttsPreviewSub.unsubscribe()
    }


    this.ttsPreviewSub = this.fileService.getTTSPreview(startData, this.data.company.id).subscribe(resp => {
      console.log("getTTSPreview", resp)
      this.preview = {
        blob: resp
      }
      // this.preview.blob = this.base64ToBlob(this.preview.audio_base64, 'audio/mpeg')
      this.preview.blobUrl = URL.createObjectURL(this.preview.blob);
      this.preview.blobSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.preview.blobUrl)
      console.log('this.preview', this.preview)
      this.loadPreview = false;
    }, error => {
      this.loadPreview = false;
    })
  }

  save() {
    this.isSubmit = true;
    let startData:any = {
      company_id: this.data.company.id,
      params: this.activeIndex.value == 0 ? {...this.form.value}: {...this.aiForm.value},
    }

    if (this.data.employee) {
      startData.employee_id = this.data.employee.id;
    }
    if (this.data.partner) {
      startData.partner_company_id = this.data.partner.partner_company_id;
    }

    if (this.activeIndex.value == 1) {
      if (!!startData.params.vibe_id) {
        startData.params.instructions = this.getVibeById(startData.params.vibe_id).value
      }

      delete startData.params.vibe_id
    }

    if (this.data.is_edit) {
      delete startData.company_id;
      delete startData.employee_id;
      delete startData.partner_company_id;
    }
    
    delete startData.params.text;


    
    console.log('startData', startData)

    this.attachSubscriptions(
      (this.data.is_edit ? 
      this.fileService.editUserSettingTTS(this.data.setting.id, startData, this.data.company.id)
      : this.fileService.createUserSettingTTS(startData, this.data.company.id)).subscribe(resp => {
        this.close()
      })
    )
    this.isSubmit = false;
  }

  start(preview:boolean = false, changeVoice?, changeVibe?) {
    if (this.data.msg) {
      let startData:any = {};
      startData = {
        company_id: this.data.msg.company_id,
        params: this.activeIndex.value == 0 ? {...this.form.value}: {...this.aiForm.value},
        // save_task_id: this.data.msg.task_id,
        // save_task_operation_id: this.data.msg.task_operation_id,
        save_discussion_post_id: this.data.msg.id,
        save_location: '/tts',
        save_filename: `TS-msgID-${this.data.msg.id}.mp3`
      }

      if (this.activeIndex.value == 1) {
        if (!!startData.params.vibe_id) {
          startData.params.instructions = this.getVibeById(startData.params.vibe_id).value
        }
  
        delete startData.params.vibe_id
      }
      delete startData.params.text;

      this.attachSubscriptions(
        this.fileService.textToSpeech(startData, this.data.company.id).subscribe(resp => {
          this.speaches.push(resp);
          this.layoutService.showSnackBar({name: 'The task for creating a voiceover has been added'}, marker("Successfully"), SnackBarItem)
          this.isSubmit = false;
        }, error => {
          this.layoutService.showSnackBar({name: error}, marker("Something went wrong"), SnackBarItem)
          this.isSubmit = false;
        })
      )
    } else {
      if (preview) {
        this.speaches = [];
        this.preview = undefined;
        this.result = undefined;
      }
      this.isSubmit = true;
      let startData:any = {};
      
      if (this.data.file) {
        startData = {
          company_id: this.data.file.company_id,
          params: this.activeIndex.value == 0 ? {...this.form.value}: {...this.aiForm.value},
          save_task_id: this.data.file.task_id,
          save_task_operation_id: this.data.file.task_operation_id,
          save_location: this.data.file.location,
          save_filename: `TS-${this.data.file.filename.split('.')[0]}.mp3`
        }
      } else {
        startData = {
          company_id: this.data.company.id,
          params: this.activeIndex.value == 0 ? {...this.form.value}: {...this.aiForm.value},
          save_task_id: 0,
          save_task_operation_id: 0,
          save_location: '/',
          save_filename: `TS-${this.data.employee.name}.mp3`
        }
      }
  
      if (this.activeIndex.value == 1) {
        // if (!!changeVoice) {
        //   startData.params.voice = changeVoice
        // }
        // if (!!changeVibe) {
        //   startData.params.vibe_id = changeVibe
        // }
        if (!!startData.params.vibe_id) {
          startData.params.instructions = this.getVibeById(startData.params.vibe_id).value
        }
        if (preview) {
          console.log('startData', startData);
          console.log('this.aiForm.value', this.aiForm.value);
    
          startData.save_location = `/temp/TTS Samples/${this.getVoiceByKey(startData.params.voice).name}`;
          startData.save_filename = `${startData.params.vibe_id ? this.getVibeById(startData.params.vibe_id).name : 'Default'} vibe.mp3`;
          startData.params.text = `You have chosen ${this.getVoiceByKey(startData.params.voice).name}'s voice ${startData.params.vibe_id ? this.getVibeById(startData.params.vibe_id).name : 'Default'}'s vibe`
          // return
        }
  
        delete startData.params.vibe_id
      }
  
      this.attachSubscriptions(
        this.fileService.textToSpeech(startData, this.data.company.id).subscribe(resp => {
          resp.is_preview = preview;
          this.speaches.push(resp);
          if (preview) {
            this.layoutService.showSnackBar({name: ''}, marker("Getting voice preview"), SnackBarItem)
          } else {
            this.layoutService.showSnackBar({name: 'The task for creating a voiceover has been added'}, marker("Successfully"), SnackBarItem)
          }
          this.isSubmit = false;
          console.log('speaches', this.speaches);
          // if (resp) {
          //   this.result = resp;
    
          //   console.log("start RESP1", resp)
          //   console.log("start result1", this.result)
    
          //   this.result.blob = this.base64ToBlob(this.result.audio_base64, 'audio/mpeg')
          //   this.result.blobUrl = URL.createObjectURL(this.result.blob);
          //   this.result.blobSrc = this.sanitizer.bypassSecurityTrustResourceUrl(this.result.blobUrl)
    
          //   console.log("start RESP2", resp)
          //   console.log("start result2", this.result)
  
          //   this.uploadFile(this.result)
          // } else {
          //   this.layoutService.showSnackBar({name: ''}, marker("Something went wrong"), SnackBarItem)
          // }
          // this.isSubmit = false;
        }, error => {
          this.layoutService.showSnackBar({name: error}, marker("Something went wrong"), SnackBarItem)
          this.isSubmit = false;
        })
      )
    }
  }

  uploadFile(audio) {
    this.attachSubscriptions(
      this.fileService.uploadFile({
        company_id: this.data.file.company_id,
        task_id: this.data.file.task_id,
        task_operation_id: this.data.file.task_operation_id,
        filesize: audio.blob.size,
        filename: `TS-${this.data.file.filename.split('.')[0]}.mp3`,
        content_type: audio.blob.type ? audio.blob.type : 'audio/mpeg',
        location: this.data.file.location,
        is_dir: 0
      }, this.data.company.id).pipe(
        tap(res => {
          audio.reportsFile = res;
          audio.place = "cp_master";
          audio.url = window.location.href;
          audio.data = res;
          audio.files = [];
          audio.task = undefined;
          audio.work = undefined;
          audio.activeLang = this.data.activeLang;
          audio.location = this.data.file.location;
          audio.target = audio.blob;
          audio.user = this.data.user;
          audio.task_id = this.data.file.task_id;
          audio.task_operation_id = this.data.file.task_operation_id;
          audio.company = this.data.company;
          audio.partition = {
            company_id: this.data.file.company_id,
            file_id: this.data.file.id,
            consist_of_file_id: audio.reportsFile.id
          };
          audio.company_id = this.data.company.id;
          audio.target_company_id = this.data.file.company_id;
          this.fileService.files$.next(audio);
        }),
        catchError((error) => {
          if (error == "Company limit exceeded") {
            this.layoutService.showSnackBar({name: ''}, marker("This company has exceeded the file upload limit."), SnackBarItem)
          } else {
            this.layoutService.showSnackBar({name: ''}, error, SnackBarItem)
          }
          return of(false)
        })
      ).subscribe(resp => {
        console.log(resp)
      })
    )
  }

  base64ToBlob(base64: string, contentType: string = '', sliceSize: number = 512): Blob {
    const byteCharacters = atob(base64); // Декодируем Base64 строку
    const byteArrays = [];
  
    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);
      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }
  
    return new Blob(byteArrays, { type: contentType });
  }

  getLangData() {
    this.attachSubscriptions(
      this.languageService.getLangData().subscribe(resp => {
        this.activeLang = resp.active;
      })
    )
  }

  close() {
    this.dialogRef.close({event: "close", data: this.data.msg && (this.result && this.result.original) ? 'update' : false});
  }

  use() {
    this.dialogRef.close({event: "use", data: this.result});
  }

  ngOnDestroy(): void {
    if (this.ttsPreviewSub) {
      this.ttsPreviewSub.unsubscribe()
    }
    this.clearSubscriptions()
  }
}
